import { ApiError, type WatchArea, type WatchCalls, type WatchLost, type WatchReason, type WatchState } from "./api";
import { dateLabel } from "./format";
import { memberName } from "./memberNames";

export const NOTE_MAX = 500;

export const STATE_TEXT: Record<WatchState, string> = {
  none: "On the sheet",
  open: "Watch open",
  passed: "Watch passed",
  failed: "Watch failed",
};

export const REASON_TEXT: Record<WatchReason, string> = {
  visited_off_app: "Visited, not recorded in the app",
  not_needed: "No visit was necessary",
  closed: "The account is closed",
  no_access: "No access to the containers",
  other: "Other",
};

export const KIND_TEXT: Record<WatchLost["kind"], string> = {
  due: "Due, no visit in the week",
  late: "No pickup for its interval and a week",
};

export const LOST_STATE_TEXT: Record<WatchLost["state"], string> = {
  open: "Open",
  visit: "Closed by a visit",
  reason: "Closed with a reason",
};

const gal = (value: number) => `${Math.round(value).toLocaleString("en-US")} gal`;

/** The areas with a watch: the areas on the app. */
export const watched = (areas: readonly WatchArea[]) => areas.filter((area) => area.state !== "none");

const day = (date: string | null | undefined) => (date ? dateLabel(date) : "–");

export const dayText = (area: WatchArea) => (!area.day ? `Starts on ${day(area.cycle_start)}` : `Day ${area.day} of ${area.days ?? 28}`);

export const cycleText = (area: Pick<WatchArea, "cycle_start" | "cycle_end">) => `${day(area.cycle_start)} to ${day(area.cycle_end)}`;

/** The count line of the watch section, with the right plural. */
export function countText(areas: number, open: number): string {
  if (!areas) return "No area on the app";
  return `${areas} ${areas === 1 ? "area" : "areas"} on the app, ${open} ${open === 1 ? "watch" : "watches"} open`;
}

/** A person of the watch: the member name, else the part of the email before "@". */
export const personText = (name: string | null | undefined) => (!name ? "" : name.includes("@") ? memberName(name) : name);

export const bandText = (found: WatchArea["gallons"]) =>
  !found ? "–" : found.low === null || found.high === null ? "No band" : `${gal(found.low)} to ${gal(found.high)}`;

export const gallonsText = (found: WatchArea["gallons"]) => (!found ? "–" : gal(found.app));

const most = (found: WatchCalls) => (found.shadow.length ? Math.max(...found.shadow) : 0);

/** The watch count and the highest count of the shadow cycles. */
export const callsText = (found: WatchCalls | undefined) => (!found ? "–" : `${found.watch} (shadow at most ${most(found)})`);

/** Rule U-5: overflows and calls at fault in one cell: "0 / 1" and "shadow at most 2 / 1". */
export const callsPair = (overflows: WatchCalls | undefined, atFault: WatchCalls | undefined): [string, string] =>
  !overflows || !atFault ? ["–", ""] : [`${overflows.watch} / ${atFault.watch}`, `shadow at most ${most(overflows)} / ${most(atFault)}`];

export interface Measure {
  key: "lost" | "rebates" | "gallons" | "calls";
  label: string;
  ok: boolean | null;
}

/** SO-4102: the four measures that must be green. Gallons have no result until the cycle ends. */
export const measures = (area: WatchArea): Measure[] => {
  const green = area.green;
  return [
    { key: "lost", label: "No open lost pickup", ok: green ? green.lost : null },
    { key: "rebates", label: "No rebate difference", ok: green ? green.rebates : null },
    { key: "gallons", label: "Gallons in the band", ok: green ? green.gallons : null },
    { key: "calls", label: "Overflows and calls at fault not above the shadow", ok: green ? green.calls : null },
  ];
};

/** 16-ux 16.6: the result in words with an icon, never a colour name. */
export const measureText = (ok: boolean | null) => (ok === null ? "At the end of the cycle" : ok ? "Met" : "Not met");

export const measureIcon = (ok: boolean | null) => (ok === null ? "…" : ok ? "✓" : "✕");

/** The form problem of a close, or null. */
export const closeProblem = (reason: WatchReason | "", note: string) => {
  if (!reason) return "Pick a reason.";
  if (note.length > NOTE_MAX) return `The note can have ${NOTE_MAX} characters at most.`;
  if (reason === "other" && !note.trim()) return "Say why in the note when the reason is Other.";
  return null;
};

/** The id of the problem line of a close form, for aria-describedby. */
export const problemId = (lost: Pick<WatchLost, "account_id" | "kind" | "due_on">) => `watch-close-problem-${lost.account_id}-${lost.kind}-${lost.due_on}`;

/** Rule T-15: only a close with a reason opens again. An older API sends no can_reopen, so no button shows. */
export const canReopen = (canEdit: boolean, lost: Pick<WatchLost, "state" | "can_reopen">) => canEdit && lost.state === "reason" && lost.can_reopen === true;

export type LostAction = "owner" | "close" | "reopen";

const NOT_DONE: Record<LostAction, string> = {
  owner: "The owner did not change.",
  close: "The lost pickup did not close.",
  reopen: "The lost pickup did not open again.",
};

/** Plain words for a failed lost pickup action, never the HTTP text (as importFailure). */
export function lostFailure(e: unknown, action: LostAction, online = typeof navigator === "undefined" || navigator.onLine): string {
  const status = e instanceof ApiError ? e.status : 0;
  if (!status) return online ? "Not saved. Try again." : "You are offline. Connect, then try again.";
  if (status === 401) return "Your session ended. Sign in again, then try again.";
  if (status === 403) return "Your role cannot change a lost pickup.";
  if (status === 404) return action === "reopen" ? "This server cannot open a lost pickup again yet." : "This lost pickup changed. Reload the page to see it now.";
  if (status === 409) return "A visit or a person changed this lost pickup. Reload the page to see it now.";
  if (status === 422) return `${NOT_DONE[action]} Check the reason and the note, then try again.`;
  if (status >= 500) return "The server did not answer. Try again in a few minutes.";
  return `${NOT_DONE[action]} Reload the page, then try again.`;
}

/** The text after a save, for the status line of the row. */
export const lostSavedText = (action: LostAction) => (action === "owner" ? "Saved" : action === "close" ? "Closed." : "Open again.");

/** A rebate difference in words: "12 gal less in the app". */
export function rebateDiffText(appGal: number, sourceGal: number): string {
  const diff = Math.round(appGal - sourceGal);
  if (!diff) return "Less than 1 gal";
  return `${Math.abs(diff).toLocaleString("en-US")} gal ${diff < 0 ? "less" : "more"} in the app`;
}

/** The weeks of a rebate difference, or "–" for an older API. */
export const weeksText = (weeks: readonly string[] | undefined) => (weeks?.length ? weeks.map((week) => dateLabel(week)).join(", ") : "–");

/** The rebate count of an area in words: "1 account differs of 1 compared". */
export function rebateCountText(rebates: WatchArea["rebates"]): string {
  if (!rebates) return "–";
  const verb = rebates.differences === 1 ? "account differs" : "accounts differ";
  return `${rebates.differences.toLocaleString("en-US")} ${verb} of ${rebates.compared.toLocaleString("en-US")} compared`;
}

export const closedText = (lost: WatchLost) =>
  lost.state === "visit"
    ? `A visit in the week of ${day(lost.visited_week)}`
    : lost.state === "reason" && lost.reason
      ? `${REASON_TEXT[lost.reason]}${lost.note ? `: ${lost.note}` : ""}${lost.closed_by ? ` (${personText(lost.closed_by)})` : ""}`
      : "–";

/** The area of `?watch=` that the Parity page opens, from a link of the Cutover page. */
export const watchParam = (search: string): string | null => new URLSearchParams(search).get("watch") || null;

/** The search of the page with `?watch=` set to the area, or removed. The other parameters stay. */
export function watchSearch(search: string, areaId: string | null): string {
  const params = new URLSearchParams(search);
  if (areaId) params.set("watch", areaId);
  else params.delete("watch");
  const found = params.toString();
  return found ? `?${found}` : "";
}

export const watchHref = (base: string, areaId: string) => `${base}/parity?watch=${encodeURIComponent(areaId)}#watch`;
